<template>
    <h-radio-group :id="`configurator-attribute-${attribute.id}`"
        :key="selected?.option.id"
        v-model="selected"
        class="space-y-xs rounded-2xs border border-solid p-sm"
        :class="{
            'border-red-100 bg-red-500/10': validate.$error,
            'border-gray-100': !validate.$error,
        }">
        <p class="flex h-6 flex-row items-center space-x-xs"
        >
            <span>
                <span>{{ attribute?.title ?? attribute?.name }}</span>

                <template v-if="selected">:</template>
            </span>

            <span v-if="selected"
                class="font-bold">
                {{ selectedOptionTitle }}
            </span>
        </p>

        <div :class="{
            'grid grid-cols-2 gap-xs lg:grid-cols-3': optionType === 'size',
            '-mt-xs flex flex-wrap': optionType === 'color' || optionType === 'image',
            'grid grid-cols-1': optionType === 'default',
        }">
            <v-page-product-configurator-option v-for="option in configurableAttribute.options"
                :key="option.option.id"
                :type="optionType"
                :value="option"
                :option="option.option"
                :prefix="configurableAttribute.attribute.prefix ?? undefined"
                :suffix="configurableAttribute.attribute.suffix ?? undefined"
                :disabled="!isAttributeAvailable || !isOptionAvailable(option)" />
        </div>

        <p v-if="selected && selectedOptionDescription"
            class="text-sm text-gray-600">
            {{ selectedOptionDescription }}
        </p>
    </h-radio-group>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { intersection } from 'es-toolkit';
import type { ViewProductQuery } from '~~/graphql';

type Configuration = Extract<ViewProductQuery['product'], { __typename: 'ConfigurableProduct' }>['configuration'];
type ConfigurableAttribute = Configuration['attributes'][0];
type ConfigurableAttributeOption = ConfigurableAttribute['options'][0];

const properties = defineProps<{
    modelValue: (ConfigurableAttributeOption | undefined)[]
    index: number
    configurableAttribute: ConfigurableAttribute
}>();

const emit = defineEmits<{
    (event: 'update:modelValue', value: (ConfigurableAttributeOption | undefined)[]): void
}>();

const availableProductIds = computed<string[] | undefined>(() => {
    if (!properties.index) {
        return undefined;
    }

    const selectedProductIds = properties.modelValue
        .slice(0, properties.index)
        .map((option) => option?.products.map((product) => product.id) || []);

    let result: string[] = [];

    for (const [index, ids] of selectedProductIds.entries()) {
        result = index === 0 ? ids : intersection(result, ids);
    }

    return result;
});

const selected = computed<ConfigurableAttributeOption | undefined>({
    get() {
        if (properties.modelValue[properties.index] === undefined) {
            return undefined;
        }

        return properties.configurableAttribute.options.find((option) => (
            option.option.id === properties.modelValue[properties.index]?.option.id
        ));
    },
    set(value) {
        const options = [...properties.modelValue];

        options[properties.index] = value;

        emit('update:modelValue', options);
    },
});

const selectedOptionTitle = computed(() => (
    selected.value
        ? (properties.configurableAttribute.attribute.prefix ?? '')
            + selected.value.option.title
            + (properties.configurableAttribute.attribute.suffix ?? '')
        : ''
));

const selectedOptionDescription = computed(() => (
    selected.value
        ? selected.value.option.description
        : undefined
));

const validate = useVuelidate({
    selected: {
        option: {
            required,
        },
    },
}, { selected }, { $autoDirty: true });

const attribute = computed(() => properties.configurableAttribute.attribute);

function isOptionAvailable(option: ConfigurableAttributeOption): boolean {
    if (availableProductIds.value === undefined) {
        return true;
    }

    return intersection(availableProductIds.value, option.products.map((product) => product.id)).length > 0;
}

watch(availableProductIds, (value) => {
    if (value && value.length === 1 && !selected.value && properties.modelValue[properties.index - 1] !== undefined) {
        const options = properties.configurableAttribute.options.filter((option) => isOptionAvailable(option));

        if (options.length === 1) {
            [selected.value] = options;
        }
    }
});

const isAttributeAvailable = computed(() => (
    !properties.index || properties.modelValue[properties.index - 1] !== undefined
));

const optionType = computed<'default' | 'color' | 'size' | 'image'>(() => {
    if (properties.configurableAttribute.options.every((option) => !!option.option.color)) {
        return 'color';
    }

    if (properties.configurableAttribute.options.every((option) => !!option.option.image)) {
        return 'image';
    }

    const isSizeAttribute = properties.configurableAttribute.options
        .map((option) => option.option.title)
        .every((title) => title.match(/[\d\s+]x[\d\s]/));

    return isSizeAttribute ? 'size' : 'default';
});
</script>
